import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { formatNumber } from '../utils'
import type {AiReindexResponse, AiSourceTypeFilter} from '@/lib/api/features/ai';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {


  postReindex,
  postReindexEstimate
} from '@/lib/api/features/ai'

interface Props {
  workspaceSlug: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmed: () => void
}

type SourceScope = 'all' | AiSourceTypeFilter

/**
 * Two-step reindex flow:
 *   1. dryRun via POST /ai/index?dryRun=true — returns the lower-bound
 *      token estimate without enqueuing anything or burning a quota slot.
 *      Renders the breakdown + `wouldExceedQuota` flag.
 *   2. Confirm via POST /ai/index — same params minus dryRun. If the
 *      estimate would exceed quota, the user has to tick `confirmCost`
 *      first; without it the backend returns 409 with a disclaimer.
 *
 * Force + sourceType controls let the admin re-embed specific subsets;
 * `force=true` is the post-template-change path (rewrites every chunk
 * regardless of content-hash stability).
 */
export function ReindexDrawer({
  workspaceSlug,
  open,
  onOpenChange,
  onConfirmed,
}: Props) {
  const { t } = useTranslation('settings')
  const [force, setForce] = useState(false)
  const [sourceScope, setSourceScope] = useState<SourceScope>('all')
  const [confirmCost, setConfirmCost] = useState(false)
  const [estimate, setEstimate] = useState<AiReindexResponse | null>(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  // Reset transient state when the drawer closes so the next open starts
  // from a clean slate. (Keeping the force/sourceScope toggle sticky is
  // tempting but more confusing — the user might forget what they picked
  // last time and reindex the wrong scope.)
  useEffect(() => {
    if (!open) {
      setEstimate(null)
      setForce(false)
      setSourceScope('all')
      setConfirmCost(false)
      setIsEstimating(false)
      setIsConfirming(false)
    }
  }, [open])

  // Fetch the dryRun estimate whenever the controls change while the drawer
  // is open. We refetch eagerly because the estimate is cheap (a few SQL
  // SUMs, no embedding work) and the user expects the figure to track their
  // changes without an explicit refresh button.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setIsEstimating(true)
    postReindexEstimate(workspaceSlug, {
      force,
      sourceType: sourceScope === 'all' ? undefined : sourceScope,
    })
      .then((data) => {
        if (cancelled) return
        setEstimate(data)
        // If the estimate now fits, clear any stale confirmCost tick so the
        // confirm button doesn't carry an unnecessary flag through.
        if (!data.estimate.wouldExceedQuota) setConfirmCost(false)
      })
      .catch((err) => {
        if (cancelled) return
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            t('aiSettings.reindex.drawer.estimateError'),
        )
      })
      .finally(() => {
        if (!cancelled) setIsEstimating(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, workspaceSlug, force, sourceScope, t])

  const handleConfirm = async () => {
    if (isConfirming) return
    setIsConfirming(true)
    try {
      await postReindex(workspaceSlug, {
        force,
        sourceType: sourceScope === 'all' ? undefined : sourceScope,
        confirmCost,
      })
      toast.success(t('aiSettings.reindex.drawer.queued'))
      onConfirmed()
      onOpenChange(false)
    } catch (err: any) {
      const data = err?.response?.data
      // 409 over-quota: backend echoes the disclaimer + estimate. Don't
      // close the drawer — surface the message so the user can tick the
      // confirm-cost box and retry without re-opening the drawer.
      if (data?.error === 'would_exceed_quota') {
        toast.error(data.message ?? t('aiSettings.reindex.drawer.quotaError'))
      } else {
        toast.error(
          data?.message ||
            err?.message ||
            t('aiSettings.reindex.drawer.confirmError'),
        )
      }
    } finally {
      setIsConfirming(false)
    }
  }

  const wouldExceed = estimate?.estimate.wouldExceedQuota ?? false
  // Disable the confirm button until we have an estimate AND, if over
  // budget, the user has explicitly ticked the cost-confirm checkbox.
  const canConfirm = !!estimate && (!wouldExceed || confirmCost) && !isConfirming

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('aiSettings.reindex.drawer.title')}</SheetTitle>
          <SheetDescription>
            {t('aiSettings.reindex.drawer.description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-auto px-4">
          {/* Scope + force controls */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('aiSettings.reindex.drawer.sourceLabel')}</Label>
              <Select
                value={sourceScope}
                onValueChange={(v: SourceScope) => setSourceScope(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('aiSettings.reindex.drawer.sourceAll')}
                  </SelectItem>
                  <SelectItem value="workItem">
                    {t('aiSettings.reindex.drawer.sourceWorkItems')}
                  </SelectItem>
                  <SelectItem value="document">
                    {t('aiSettings.reindex.drawer.sourceDocuments')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start justify-between rounded-md border p-3">
              <div className="pr-4">
                <Label className="text-sm font-medium">
                  {t('aiSettings.reindex.drawer.forceLabel')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('aiSettings.reindex.drawer.forceHint')}
                </p>
              </div>
              <Switch checked={force} onCheckedChange={setForce} />
            </div>
          </div>

          {/* Estimate display */}
          <div className="space-y-2 rounded-md border bg-muted/40 p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('aiSettings.reindex.drawer.estimateLabel')}
            </p>
            {isEstimating ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('aiSettings.reindex.drawer.estimating')}
              </div>
            ) : !estimate ? (
              <p className="text-sm text-muted-foreground">
                {t('aiSettings.reindex.drawer.noEstimate')}
              </p>
            ) : (
              <>
                <p className="text-2xl font-semibold tabular-nums">
                  ~
                  {formatNumber(estimate.estimate.totalEstimatedTokensLowerBound)}{' '}
                  <span className="text-sm font-normal text-muted-foreground">
                    {t('aiSettings.reindex.drawer.tokens')}
                  </span>
                </p>
                <div className="grid grid-cols-2 gap-2 pt-1 text-xs text-muted-foreground">
                  <span>
                    {t('aiSettings.reindex.drawer.workItems')}:{' '}
                    {formatNumber(estimate.estimate.workItems.count)}
                  </span>
                  <span>
                    {t('aiSettings.reindex.drawer.documents')}:{' '}
                    {formatNumber(estimate.estimate.documents.count)}
                  </span>
                  <span>
                    {t('aiSettings.reindex.drawer.remainingBudget')}:{' '}
                    {estimate.estimate.remainingMonthlyBudget === null
                      ? t('aiSettings.reindex.drawer.unlimited')
                      : formatNumber(estimate.estimate.remainingMonthlyBudget)}
                  </span>
                </div>
                <p className="pt-2 text-xs italic text-muted-foreground">
                  {estimate.estimate.disclaimer}
                </p>
              </>
            )}
          </div>

          {/* Over-quota cost-confirm */}
          {wouldExceed && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t('aiSettings.reindex.drawer.quotaAlertTitle')}
              </AlertTitle>
              <AlertDescription>
                <p className="mb-2">
                  {t('aiSettings.reindex.drawer.quotaAlertBody')}
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmCost}
                    onChange={(e) => setConfirmCost(e.target.checked)}
                    className="size-4 rounded border"
                  />
                  {t('aiSettings.reindex.drawer.confirmCostLabel')}
                </label>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="flex-row justify-end gap-2">
          <SheetClose asChild>
            <Button variant="outline" disabled={isConfirming}>
              {t('aiSettings.reindex.drawer.cancel')}
            </Button>
          </SheetClose>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {isConfirming ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
            {t('aiSettings.reindex.drawer.confirm')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
